import log from 'loglevel';
import * as React from 'react';
import { connect } from 'react-redux';
import { Set, List } from 'immutable';
import { LocationInfo } from '../../../../../../types';
import {
    behavior as behaviorSelectors,
    config as configSelectors,
} from '../../../../../../store/selectors';
import {
    showGreeting as showGreetingAction,
    hideGreeting as hideGreetingAction,
    hideAllGreetings as hideAllGreetingsAction,
} from '../../../../../../store/actions';
import { TasksBackendClient } from '../../../../../../backends/tasks';
import Greeting from '../Greeting';

type ReduxState = any;

export interface GreetingsDirectorProps {
    openChat: () => void;
    hideAll?: boolean;
}

interface StateProps {
    pendingGreetings: List<any>;
    showingGreetings: Set<string>;
    tasksBackendClient: TasksBackendClient;
    locationInfo: LocationInfo;
    userInfoExists: boolean;
    customerId?: string;
    userId?: string;
    intermediateDelayMs?: number;
}

interface DispatchProps {
    showGreeting: (id: string) => void;
    hideGreeting: (id: string) => void;
    hideAllGreetings: () => void;
}

type AllProps = GreetingsDirectorProps & StateProps & DispatchProps;

const GreetingsDirector: React.FC<AllProps> = ({
    pendingGreetings,
    openChat,
    hideAll,
    showGreeting,
    hideGreeting,
    hideAllGreetings,
    showingGreetings,
    intermediateDelayMs,
    tasksBackendClient,
    locationInfo,
    userInfoExists,
    customerId,
    userId,
}: AllProps) => {
    const [currentIndex, setCurrentIndex] = React.useState<number>(-1);
    const [currentGreeting, setCurrentGreeting] = React.useState<any>({});
    const incIndexTimer = React.useRef<ReturnType<typeof global.setTimeout> | undefined>(undefined);

    const reset = React.useCallback(() => {
        clearTimeout(incIndexTimer.current);
        hideAllGreetings();
        setCurrentIndex(-1);
    }, []);

    React.useEffect(() => {
        return () => clearTimeout(incIndexTimer.current);
    }, []);

    React.useEffect(() => {
        reset();
    }, [pendingGreetings]);

    React.useEffect(() => {
        log.debug('setCurrentGreeting', currentIndex, pendingGreetings?.toJS());

        if (currentIndex >= 0) {
            setCurrentGreeting(pendingGreetings.get(currentIndex));
        } else {
            setCurrentGreeting(undefined);

            const showFirstTimer = setTimeout(() => setCurrentIndex(0), 10);

            return () => clearTimeout(showFirstTimer);
        }
    }, [pendingGreetings, currentIndex]);

    const onShow = React.useCallback(() => {
        currentGreeting && showGreeting(currentGreeting.id);
    }, [currentGreeting]);

    const onHide = React.useCallback(() => {
        currentGreeting && hideGreeting(currentGreeting.id);

        incIndexTimer.current = setTimeout(() => {
            setCurrentIndex(currentIndex + 1);
        }, intermediateDelayMs);
    }, [currentGreeting, currentIndex, intermediateDelayMs]);

    return currentGreeting ? (
        <Greeting
            {...currentGreeting}
            key={currentGreeting.id}
            tasksBackendClient={tasksBackendClient}
            locationInfo={locationInfo}
            userInfoExists={userInfoExists}
            customerId={customerId}
            userId={userId}
            isShowing={showingGreetings.has(currentGreeting.id)}
            forceHide={hideAll}
            onShow={onShow}
            onHide={onHide}
            openChat={openChat}
        />
    ) : null;
};

const mapStateToProps = (state: ReduxState, ownProps: GreetingsDirectorProps): StateProps => {
    const pendingGreetings = behaviorSelectors.pendingGreetings(state?.behavior);
    const showingGreetings = state?.behavior?.get('showingGreetings') || Set();
    const tasksBackendClient = state?.config?.get('tasksBackendClient');
    const locationInfo = state?.config?.get('browserLocation');
    const userInfoExists = state?.config?.get('userInfoExists');
    const customerId = state?.config?.get('customerId');
    const userId = state?.config?.get('userId');
    const intermediateDelayMs = behaviorSelectors.greetingsDelayMs(state?.behavior);

    return {
        pendingGreetings,
        showingGreetings,
        tasksBackendClient,
        locationInfo,
        userInfoExists,
        customerId,
        userId,
        intermediateDelayMs,
    };
};

const mapDispatchToProps = (
    dispatch: React.Dispatch<any>,
    ownProps: GreetingsDirectorProps
): DispatchProps => {
    return {
        showGreeting: (id: string) => dispatch(showGreetingAction(id)),
        hideGreeting: (id: string) => dispatch(hideGreetingAction(id)),
        hideAllGreetings: () => dispatch(hideAllGreetingsAction()),
    };
};

export default connect<StateProps, DispatchProps, GreetingsDirectorProps>(
    mapStateToProps,
    mapDispatchToProps
)(GreetingsDirector);
